import { RefObject, useEffect, useState } from 'react';

interface Options {
  /**
   * The element that is used as the viewport for checking visibility of the target.
   * Must be the ancestor of the target.
   * Defaults to the browser viewport if not specified or if null.   */
  root: Element | null;
  /**
   * This set of values serves to grow or shrink each side of the root element's bounding box before computing intersections,
   * the options are similar to those of margin in CSS.
   */
  rootMargin: string;
  /**
   * Either a single number or an array of numbers
   * which indicate at what percentage of the target's visibility the observer's callback should be executed,
   * ranges from 0 to 1.0,
   * where 1.0 means every pixel is visible in the viewport.
   */
  threshold: number;
}

const useElementOnScreen = (
  containerRef: RefObject<Element>,
  options: Options,
) => {
  const [currentElement, setCurrentElement] = useState<Element | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const callbackFunction = (entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    setIsVisible(entry.isIntersecting);
  };

  useEffect(() => {
    const node = containerRef?.current;
    const hasIOSupport = !!window.IntersectionObserver;

    if (!hasIOSupport || !node) return;

    setCurrentElement(containerRef.current);

    const observer = new IntersectionObserver(callbackFunction, options);

    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => observer.disconnect();

    // eslint-disable-next-line
  }, [containerRef, options]);

  return isVisible;
};

export default useElementOnScreen;
